/**
 * SystemSealedSecretTool - 多环境版 SealedSecret 加密工具
 *
 * 与 SealedSecretTool 不同的是，这个版本不依赖 ArgoCD，
 * 而是从 useEnvironments hook 获取 K8s 环境列表，让用户选环境后加密。
 */
import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  makeStyles,
  IconButton,
  Snackbar,
  CircularProgress,
  Chip,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import LockIcon from '@material-ui/icons/Lock';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';

interface EnvironmentOption {
  label: string;
  cluster: string;
  namespace: string;
}

interface ControllerInfo {
  controllerName: string;
  controllerNamespace: string;
  serviceName: string;
  version?: string;
}

interface KeyValuePair {
  key: string;
  value: string;
  encryptedValue?: string;
  loading?: boolean;
  error?: string;
}

const useStyles = makeStyles(theme => ({
  envSelector: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.04)' : '#f8f9fa',
    border: `1px solid ${theme.palette.divider}`,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    maxHeight: 'calc(100vh - 400px)',
    overflow: 'auto',
    paddingRight: theme.spacing(1),
  },
  resultBox: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    wordBreak: 'break-all' as const,
    position: 'relative' as const,
    border: `1px solid ${theme.palette.divider}`,
  },
  copyButton: {
    position: 'absolute' as const,
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
  clusterInfo: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap' as const,
    marginBottom: theme.spacing(2),
  },
  chip: { height: 28 },
  controllerInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
  },
  successIcon: { color: theme.palette.success?.main || '#4caf50' },
  keyValuePaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
  },
  keyValueRow: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'flex-start',
  },
  keyValueIndex: {
    minWidth: 24,
    height: 24,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.75rem',
    marginTop: theme.spacing(1),
  },
  resultArea: {
    maxHeight: 'calc(100vh - 400px)',
    overflow: 'auto',
    paddingRight: theme.spacing(1),
  },
  yamlBox: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    wordBreak: 'break-all' as const,
    position: 'relative' as const,
    border: `1px solid ${theme.palette.divider}`,
  },
}));

interface SystemSealedSecretToolProps {
  environments: EnvironmentOption[];
}

export const SystemSealedSecretTool: React.FC<SystemSealedSecretToolProps> = ({ environments }) => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [selectedEnvIdx, setSelectedEnvIdx] = useState(0);
  const [controllerInfo, setControllerInfo] = useState<ControllerInfo | null>(null);
  const [controllerLoading, setControllerLoading] = useState(false);
  const [controllerError, setControllerError] = useState('');

  const [secretName, setSecretName] = useState('');
  const [secretNamespace, setSecretNamespace] = useState('');
  const [keyValuePairs, setKeyValuePairs] = useState<KeyValuePair[]>([{ key: '', value: '' }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const selectedEnv = environments[selectedEnvIdx];

  // 检测 SealedSecret Controller
  const checkController = useCallback(async (clusterName: string) => {
    setControllerLoading(true);
    setControllerError('');
    setControllerInfo(null);

    try {
      const baseUrl = await discoveryApi.getBaseUrl('sealed-secret-api');
      const response = await fetchApi.fetch(
        `${baseUrl}/detect-controller?clusterName=${encodeURIComponent(clusterName)}`
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || `检测失败: ${response.status}`);
      }

      const controller = await response.json();
      setControllerInfo(controller);
    } catch (err) {
      setControllerError(err instanceof Error ? err.message : '检测 SealedSecret Controller 失败');
    } finally {
      setControllerLoading(false);
    }
  }, [discoveryApi, fetchApi]);

  // 环境切换时自动检测 controller
  useEffect(() => {
    if (selectedEnv) {
      setSecretNamespace(selectedEnv.namespace);
      checkController(selectedEnv.cluster);
    }
  }, [selectedEnv, checkController]);

  const addKeyValuePair = () => {
    setKeyValuePairs([...keyValuePairs, { key: '', value: '' }]);
  };

  const removeKeyValuePair = (index: number) => {
    if (keyValuePairs.length > 1) {
      setKeyValuePairs(keyValuePairs.filter((_, i) => i !== index));
    }
  };

  const updateKeyValuePair = (index: number, field: 'key' | 'value', newValue: string) => {
    const updated = [...keyValuePairs];
    updated[index] = { ...updated[index], [field]: newValue };
    setKeyValuePairs(updated);
  };

  const handleEncrypt = async () => {
    if (!secretName || !secretNamespace) {
      setError('请填写 Secret 名称和命名空间');
      return;
    }
    const validPairs = keyValuePairs.filter(p => p.key && p.value);
    if (validPairs.length === 0) {
      setError('请至少填写一个 Key-Value 对');
      return;
    }
    if (!controllerInfo || !selectedEnv) {
      setError('未检测到 SealedSecret Controller');
      return;
    }

    setLoading(true);
    setError(null);
    setKeyValuePairs(prev => prev.map(p => ({ ...p, encryptedValue: undefined, error: undefined })));

    const baseUrl = await discoveryApi.getBaseUrl('sealed-secret-api');

    const results = await Promise.all(
      keyValuePairs.map(async (pair, index) => {
        if (!pair.key || !pair.value) {
          return { index, encryptedValue: undefined, error: undefined };
        }
        try {
          const response = await fetchApi.fetch(`${baseUrl}/encrypt`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              secretName,
              secretNamespace,
              secretValue: pair.value,
              clusterName: selectedEnv.cluster,
              controllerNamespace: controllerInfo.controllerNamespace,
              serviceName: controllerInfo.serviceName,
            }),
          });

          if (!response.ok) {
            const data = await response.json();
            throw new Error(data.error || '加密失败');
          }
          const data = await response.json();
          return { index, encryptedValue: data.encryptedValue, error: undefined };
        } catch (err) {
          return { index, encryptedValue: undefined, error: err instanceof Error ? err.message : '加密失败' };
        }
      })
    );

    setKeyValuePairs(prev => {
      const updated = [...prev];
      results.forEach(r => {
        updated[r.index] = { ...updated[r.index], encryptedValue: r.encryptedValue, error: r.error };
      });
      return updated;
    });
    setLoading(false);
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setSnackbarOpen(true);
  };

  const generateYaml = () => {
    const encryptedPairs = keyValuePairs.filter(p => p.key && p.encryptedValue);
    if (encryptedPairs.length === 0) return '';
    const lines = encryptedPairs.map(p => `    ${p.key}: ${p.encryptedValue}`).join('\n');
    return `apiVersion: bitnami.com/v1alpha1
kind: SealedSecret
metadata:
  name: ${secretName}
  namespace: ${secretNamespace}
spec:
  encryptedData:
${lines}`;
  };

  const hasEncryptedResults = keyValuePairs.some(p => p.encryptedValue);

  return (
    <Box>
      {/* 环境选择器 */}
      <Box className={classes.envSelector}>
        <Typography variant="subtitle2" gutterBottom>选择目标环境</Typography>
        <FormControl variant="outlined" size="small" fullWidth>
          <InputLabel>K8s 环境</InputLabel>
          <Select
            value={selectedEnvIdx}
            onChange={(e) => setSelectedEnvIdx(e.target.value as number)}
            label="K8s 环境"
          >
            {environments.map((env, idx) => (
              <MenuItem key={idx} value={idx}>
                {env.label} — {env.cluster} / {env.namespace}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {selectedEnv && (
          <Box className={classes.clusterInfo} style={{ marginTop: 8, marginBottom: 0 }}>
            <Chip className={classes.chip} label={`集群: ${selectedEnv.cluster}`} variant="outlined" size="small" />
            <Chip className={classes.chip} label={`命名空间: ${selectedEnv.namespace}`} variant="outlined" size="small" />
          </Box>
        )}
      </Box>

      {/* Controller 状态 */}
      {controllerLoading && (
        <Box className={classes.controllerInfo}>
          <CircularProgress size={16} />
          <Typography variant="body2">正在检测 SealedSecret Controller...</Typography>
        </Box>
      )}

      {controllerError && (
        <Alert severity="error" style={{ marginBottom: 16 }}>{controllerError}</Alert>
      )}

      {controllerInfo && (
        <Box className={classes.controllerInfo}>
          <CheckCircleIcon className={classes.successIcon} fontSize="small" />
          <Typography variant="body2">
            Controller: {controllerInfo.controllerNamespace}/{controllerInfo.controllerName}
            {controllerInfo.version && ` (${controllerInfo.version})`}
          </Typography>
        </Box>
      )}

      {/* 表单和结果 */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Box className={classes.form}>
            <TextField
              label="Secret 名称"
              variant="outlined"
              size="small"
              fullWidth
              required
              value={secretName}
              onChange={(e) => setSecretName(e.target.value)}
              placeholder="my-secret"
            />
            <TextField
              label="Secret 命名空间"
              variant="outlined"
              size="small"
              fullWidth
              required
              value={secretNamespace}
              onChange={(e) => setSecretNamespace(e.target.value)}
              placeholder="default"
            />

            <Typography variant="subtitle2" style={{ marginTop: 8 }}>
              Key-Value 对
            </Typography>

            {keyValuePairs.map((pair, index) => (
              <Paper key={index} className={classes.keyValuePaper} variant="outlined">
                <Box className={classes.keyValueRow}>
                  <Box className={classes.keyValueIndex}>{index + 1}</Box>
                  <Box flex={1}>
                    <TextField
                      label="Key"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={pair.key}
                      onChange={(e) => updateKeyValuePair(index, 'key', e.target.value)}
                      placeholder="password"
                      style={{ marginBottom: 8 }}
                    />
                    <TextField
                      label="Value (明文)"
                      variant="outlined"
                      size="small"
                      fullWidth
                      multiline
                      minRows={2}
                      value={pair.value}
                      onChange={(e) => updateKeyValuePair(index, 'value', e.target.value)}
                      placeholder="输入需要加密的敏感数据"
                    />
                    {pair.error && (
                      <Typography variant="caption" color="error" style={{ marginTop: 4, display: 'block' }}>
                        {pair.error}
                      </Typography>
                    )}
                    {pair.encryptedValue && (
                      <Typography variant="caption" color="primary" style={{ marginTop: 4, display: 'block' }}>
                        ✓ 已加密
                      </Typography>
                    )}
                  </Box>
                  {keyValuePairs.length > 1 && (
                    <IconButton size="small" onClick={() => removeKeyValuePair(index)} title="删除">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Paper>
            ))}

            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={addKeyValuePair}
            >
              添加 Key-Value
            </Button>

            <Button
              variant="contained"
              color="primary"
              startIcon={<LockIcon />}
              onClick={handleEncrypt}
              disabled={loading || !controllerInfo}
              style={{ marginTop: 16 }}
            >
              {loading ? '加密中...' : '加密全部'}
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12} md={7}>
          {error && <Alert severity="error" style={{ marginBottom: 16 }}>{error}</Alert>}

          {hasEncryptedResults ? (
            <Box className={classes.resultArea}>
              <Typography variant="subtitle2" gutterBottom>加密结果:</Typography>
              {keyValuePairs.filter(p => p.key && p.encryptedValue).map((pair, index) => (
                <Box key={index} style={{ marginBottom: 16 }}>
                  <Typography variant="caption" color="textSecondary">{pair.key}:</Typography>
                  <Box className={classes.resultBox}>
                    <IconButton className={classes.copyButton} size="small" onClick={() => handleCopy(pair.encryptedValue!)} title="复制">
                      <FileCopyIcon fontSize="small" />
                    </IconButton>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', paddingRight: 32 }}>{pair.encryptedValue}</pre>
                  </Box>
                </Box>
              ))}

              <Typography variant="subtitle2" gutterBottom style={{ marginTop: 24 }}>完整 SealedSecret YAML:</Typography>
              <Box className={classes.yamlBox}>
                <IconButton className={classes.copyButton} size="small" onClick={() => handleCopy(generateYaml())} title="复制 YAML">
                  <FileCopyIcon fontSize="small" />
                </IconButton>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', paddingRight: 32 }}>{generateYaml()}</pre>
              </Box>
            </Box>
          ) : (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100%"
              minHeight={300}
              style={{ backgroundColor: '#f5f5f5', borderRadius: 4 }}
            >
              <Typography variant="body2" color="textSecondary">
                选择环境 → 填写表单 → 点击"加密全部"
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message="已复制到剪贴板"
      />
    </Box>
  );
};

export default SystemSealedSecretTool;
